import type { Theme } from '@mui/material/styles';
import { type SystemStyleObject } from '@mui/system';

export const getHeroContainerStyles = (providerHeroTheme: string, theme: Theme, hasProviderPhotos: boolean) => ({
	display: 'flex',
	flexDirection: { xs: 'column-reverse', md: 'row' },
	backgroundImage:
		providerHeroTheme === 'light'
			? theme.palette.backgrounds?.lightGradient || ''
			: theme.palette.backgrounds?.darkGradient,
	...(hasProviderPhotos && { maxHeight: { xs: 'auto', md: 'calc(100vh - 12.5rem)' } }),
	...(!hasProviderPhotos && { height: 'auto' }),
});

export const getHeroImageWrapperStyles = {
	width: { xs: '100%', md: '50%' },
	height: { xs: '100%', md: 'auto' },
	//added because PDP hero needs image to be static or it looks weird
	'[data-test-id="section_aspect_ratio_padding_box"]': {
		position: { md: 'static' },
	},
	'.MuiBox-root > img.MuiBox-root': {
		objectFit: 'cover !important',
		objectPosition: { xs: 'center center !important', md: 'top left !important' },
	},
};

export const getHeroContentContainerStyles = (theme: Theme, hasProviderPhotos: boolean): SystemStyleObject<Theme> => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '1rem',
	px: { xs: 3, md: '2%', lg: '5%' },
	py: { xs: '2.5rem', md: '3.75rem' },
	[theme.breakpoints.up('md')]: {
		width: hasProviderPhotos ? '50%' : '100%',
		...(!hasProviderPhotos ? { alignItems: 'center' } : {}),
	},
});

export const heroTitleStyles = {
	fontWeight: '400',
	letterSpacing: '-1px',
	textAlign: 'left',
};

export const heroQuoteStyles = {
	fontWeight: 600,
};

export const getHeroLabelsListStyles = (hasProviderPhotos: boolean) => ({
	display: 'flex',
	flexDirection: hasProviderPhotos ? 'column' : 'row',
	alignItems: hasProviderPhotos ? 'flex-start' : 'center',
	justifyContent: hasProviderPhotos ? 'flex-start' : 'center',
	gap: '1rem',
	padding: { xs: '2rem 0', md: '2.875rem 0' },
});

export const getHeroLabelsItemStyles = (textColor: string) => ({
	display: 'flex',
	gap: '.5rem',
	flexGrow: { xs: '1', md: '0' },
	color: textColor,
	justifyContent: 'flex-start',
});

export const getHeroLabelsButtonStyles = (textColor: string) => ({
	display: 'flex',
	flexGrow: { xs: '1', md: '0' },
	color: textColor,
	justifyContent: 'flex-start',
	padding: '0',
});

export const getProviderContentContainerStyles = {
	display: 'flex',
	flexDirection: 'column',
	gap: '1rem',
	px: { xs: 3, md: '2%', lg: '5%' },
	py: { xs: '1.25rem', md: '1.875rem' },
};

export const providerAboutTitleStyles = {
	paddingBottom: '.5rem',
};

export const aboutListStyles = {
	display: 'flex',
	gap: '1rem',
	flexWrap: 'wrap',
};

export const aboutListItemStyles = {
	width: { xs: '100%', md: '49%' },
};

export const aboutSubTitleListStyles = {
	display: 'flex',
	flexWrap: 'wrap',
	paddingBottom: '2rem',
};

export const aboutSubTitleListMiddotStyles = {
	px: '.25rem',
};

export const providerAboutSubTitleListBoxStyle = {
	display: 'flex',
};

export const aboutListItemTitleStyles = {
	pb: '0.25rem',
};

export const tabLinkStyles = {
	'&.active': {
		'& .MuiTypography-root': {
			borderBottom: '2px solid',
			borderColor: 'text.interactive',
			color: 'text.interactive',
			fontWeight: 600,
		},
	},
	textDecoration: 'none',
};

export const tabLinkTextStyles = {
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	height: '3rem',
	lineHeight: '3rem',
	display: 'inline-block', // makes the link 100% height so that user doesn't have to click exactly on the text
	padding: '0 1rem',
	fontSize: '1rem',
	color: 'text.tertiary',
};

export const tabContainerStyles = {
	display: 'flex',
	overflow: 'auto',
	justifyContent: 'center',
};

export const specialtiesListStyles = {
	display: 'flex',
	flexWrap: 'wrap',
	gap: '0.5rem',
};

export const galleryWrapperStyles = {
	display: 'flex',
	gap: '1.25rem',
	flexWrap: 'wrap',
};

export const getGalleryImageWrapperStyles = {
	width: { xs: '100%', md: '49%' },
	height: 'auto',
	maxHeight: '38.125rem',
};

export const providerOfficesTitleStyles = {
	textAlign: 'center',
};

export const getMapAndLocationsComponentStyles = (isSmallScreen?: boolean, fullWidth?: boolean) => ({
	display: 'flex',
	height: isSmallScreen ? 'auto' : 'calc(100vh - 12.5rem)',
	borderRadius: fullWidth ? '0' : '0.5rem',
	marginBottom: fullWidth ? '0' : '1.25rem',
	justifyContent: 'flex-start',
});

export const getLocationsCardContainerStyles = (theme: Theme, isSmallScreen?: boolean) => ({
	display: 'flex',
	flexDirection: isSmallScreen ? 'row' : 'column',
	flexWrap: 'nowrap',
	borderBottom: isSmallScreen ? 'none' : '0.0625rem solid',
	borderLeft: isSmallScreen ? 'none' : '0.0625rem solid',
	borderTop: isSmallScreen ? 'none' : '0.0625rem solid',
	borderRadius: isSmallScreen ? '0.5rem' : '0.5rem 0rem 0rem 0.5rem',
	borderColor: 'neutrals.primary',
	backgroundColor: isSmallScreen ? `${theme.palette.backgrounds?.white}` : `${theme.palette.backgrounds?.light}`,
	overflow: 'scroll',
	paddingTop: '1rem',
	paddingBottom: '1rem',
	px: isSmallScreen ? '1rem' : '',
	'::-webkit-scrollbar': {
		display: 'none',
	},
	msOverflowStyle: 'none',
	scrollBarWidth: 'none',
	height: { xs: 'auto', md: '100%' },
	position: 'relative',
});
